import { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import heroImg0 from '../../assets/manufacturing-hero.jpg'
import heroImg1 from '../../assets/manufacturing-hero1.jpg'
import { ChevronLeft, ChevronRight } from 'lucide-react'

const slides = [
  {
    key: 'api',
    image: heroImg0,
    eyebrow: 'API Development',
    title: 'Route design and synthesis planning',   
    cta1: { label: 'Request a Quote', to: '/contact' },
    cta2: { label: 'Explore Services', to: '/services' },
  },
  {
    key: 'drugs',
    image: heroImg1,
    eyebrow: 'Drug Product Support',
    title: 'Scalable workflows with QC checkpoints',
  
    cta1: { label: 'Talk to Experts', to: '/contact' },
    cta2: { label: 'Quality Assurance', to: '/quality-assurance' },
  },
  {
    key: 'products',
    image: heroImg1,
    eyebrow: 'Product Solutions',
    title: 'Validation support & documentation-first',
   
    cta1: { label: 'View Products', to: '/products' },
    cta2: { label: 'Research & Development', to: '/research-development' },
  },
]


export default function HomeSlider() {
  const [active, setActive] = useState(0)

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false
  }, [])

  useEffect(() => {
    if (prefersReducedMotion) return
    const id = setInterval(() => {
      setActive((v) => (v + 1) % slides.length)
    }, 4500)
    return () => clearInterval(id)
  }, [prefersReducedMotion])

  return (
    <section aria-label="Home slider" className="relative overflow-hidden bg-white">
      {/* Background image */}
      <div aria-hidden="true" className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${slides[active].image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/15 via-white/60 to-brand-accent/15" />
      </div>


      <div className="relative mx-auto max-w-7xl px-4 pt-6 sm:pt-8">
        <div className="relative rounded-3xl border border-slate-200 bg-white/70 backdrop-blur shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
            <div className="lg:col-span-7">
              <div className="p-6 sm:p-8 lg:py-10 lg:px-10">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand-primary/20 bg-white/70 px-4 py-2 text-brand-primary text-xs sm:text-sm font-semibold shadow-sm w-fit">
                  <span className="w-2 h-2 rounded-full bg-brand-accent" />
                  {slides[active].eyebrow}
                </div>

                <motion.h2
                  key={slides[active].key}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: 'easeOut' }}
                  className="text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight text-slate-900"
                >
                  {slides[active].title}
                </motion.h2>

                <motion.p
                  key={`${slides[active].key}-desc`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                  className="mt-4 text-sm sm:text-[16px] text-slate-700 leading-relaxed max-w-2xl"
                >
                  {slides[active].desc}
                </motion.p>

                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <a
                    href={slides[active].cta1.to}
                    className="inline-flex items-center justify-center rounded-2xl bg-brand-primary text-white px-5 py-3 text-sm sm:text-base font-semibold hover:brightness-110 transition shadow-sm"
                  >
                    {slides[active].cta1.label}
                  </a>
                  <a
                    href={slides[active].cta2.to}
                    className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm sm:text-base font-semibold text-slate-800 hover:bg-slate-50 transition"
                  >
                    {slides[active].cta2.label}
                  </a>
                </div>

                <div className="mt-8 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2" role="tablist" aria-label="Slider pagination">
                    {slides.map((s, idx) => (
                      <button
                        key={s.key}
                        type="button"
                        aria-label={`Go to slide ${idx + 1}`}
                        aria-selected={idx === active}
                        onClick={() => setActive(idx)}
                        className={`w-10 h-2 rounded-full transition overflow-hidden ${
                          idx === active ? 'bg-brand-primary' : 'bg-slate-200 hover:bg-slate-300'
                        }`}
                      />
                    ))}
                  </div>

                  <div className="hidden sm:flex items-center gap-2">
                    <button
                      type="button"
                      aria-label="Previous slide"
                      onClick={() => setActive((v) => (v - 1 + slides.length) % slides.length)}
                      className="w-10 h-10 rounded-2xl border border-slate-200 bg-white/80 hover:bg-white transition flex items-center justify-center text-slate-700"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      type="button"
                      aria-label="Next slide"
                      onClick={() => setActive((v) => (v + 1) % slides.length)}
                      className="w-10 h-10 rounded-2xl border border-slate-200 bg-white/80 hover:bg-white transition flex items-center justify-center text-slate-700"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 border-t lg:border-t-0 lg:border-l border-slate-200">
              <div className="relative h-52 sm:h-64 lg:h-full">
                <div className="absolute inset-0">
                  <img
                    src={slides[active].image}
                    alt={slides[active].eyebrow}
                    className="h-full w-full object-cover opacity-95"
                    loading="eager"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="rounded-2xl border border-white/30 bg-black/25 backdrop-blur px-4 py-3">
                    <div className="text-white text-sm font-bold">Fast response for enquiries</div>
                    <div className="text-white/85 text-xs mt-1">Send your requirement—our team will assist with feasibility.</div>
                  </div>
                </div>

                {/* Mobile controls */}
                <div className="absolute top-4 right-4 flex sm:hidden items-center gap-2">
                  <button
                    type="button"
                    aria-label="Previous slide"
                    onClick={() => setActive((v) => (v - 1 + slides.length) % slides.length)}
                    className="w-10 h-10 rounded-2xl border border-white/30 bg-black/25 hover:bg-black/35 transition flex items-center justify-center text-white"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    type="button"
                    aria-label="Next slide"
                    onClick={() => setActive((v) => (v + 1) % slides.length)}
                    className="w-10 h-10 rounded-2xl border border-white/30 bg-black/25 hover:bg-black/35 transition flex items-center justify-center text-white"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reduce outer padding on very small screens */}
        <div className="h-6 sm:h-8" />
      </div>
    </section>
  )
}

